/**
 * Lazy Load Scripts - Optimización de carga de scripts de terceros
 * Coloca este archivo en assets/lazy-load-scripts.js
 */

(function() {
  'use strict';

  // Configuración de scripts a cargar
  const thirdPartyScripts = {
    gsap: {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js',
      priority: 'high', // Se carga primero si es necesario
      loadOn: 'scroll', // 'scroll', 'interaction', 'idle', o 'immediate'
    },
    scrollTrigger: {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js',
      priority: 'high',
      loadOn: 'scroll',
      dependsOn: 'gsap' // Espera a que GSAP se cargue primero
    },
    // Agrega aquí otros scripts de terceros
    // chat: {
    //   src: 'URL_DEL_CHAT',
    //   priority: 'low',
    //   loadOn: 'interaction'
    // }
  };

  // Estado de carga de scripts
  const loadedScripts = {};
  const loadingScripts = {};

  /**
   * Carga un script de forma asíncrona
   */
  function loadScript(name, config) {
    return new Promise((resolve, reject) => {
      // Si ya está cargado, resolver inmediatamente
      if (loadedScripts[name]) {
        resolve();
        return;
      }

      // Si ya se está cargando, esperar a que termine
      if (loadingScripts[name]) {
        loadingScripts[name].push(resolve);
        return;
      }

      // Iniciar cola de espera
      loadingScripts[name] = [resolve];

      // Verificar dependencias
      if (config.dependsOn && !loadedScripts[config.dependsOn]) {
        const depConfig = thirdPartyScripts[config.dependsOn];
        loadScript(config.dependsOn, depConfig).then(() => {
          actuallyLoadScript(name, config);
        });
      } else {
        actuallyLoadScript(name, config);
      }
    });
  }

  /**
   * Carga el script real
   */
  function actuallyLoadScript(name, config) {
    const script = document.createElement('script');
    script.src = config.src;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      loadedScripts[name] = true;
      console.log(`✓ ${name} loaded`);
      
      // Resolver todas las promesas en espera
      if (loadingScripts[name]) {
        loadingScripts[name].forEach(resolve => resolve());
        delete loadingScripts[name];
      }

      // Disparar evento personalizado
      document.dispatchEvent(new CustomEvent(`script:${name}:loaded`));
    };

    script.onerror = () => {
      console.error(`✗ Error loading ${name}`);
      delete loadingScripts[name];
    };

    document.head.appendChild(script);
  }

  /**
   * Inicializar carga de scripts según su configuración
   */
  function initScriptLoading() {
    let hasScrolled = false;
    let hasInteracted = false;

    // Scripts de alta prioridad que necesitan scroll
    const scrollScripts = Object.entries(thirdPartyScripts)
      .filter(([_, config]) => config.loadOn === 'scroll');

    // Scripts de baja prioridad que esperan interacción
    const interactionScripts = Object.entries(thirdPartyScripts)
      .filter(([_, config]) => config.loadOn === 'interaction');

    // Scripts que se cargan en idle
    const idleScripts = Object.entries(thirdPartyScripts)
      .filter(([_, config]) => config.loadOn === 'idle');

    // Scripts inmediatos
    const immediateScripts = Object.entries(thirdPartyScripts)
      .filter(([_, config]) => config.loadOn === 'immediate');

    // Cargar scripts inmediatos
    immediateScripts.forEach(([name, config]) => {
      loadScript(name, config);
    });

    // Cargar scripts en idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        idleScripts.forEach(([name, config]) => {
          loadScript(name, config);
        });
      }, { timeout: 2000 });
    } else {
      setTimeout(() => {
        idleScripts.forEach(([name, config]) => {
          loadScript(name, config);
        });
      }, 2000);
    }

    // Cargar scripts al hacer scroll (solo una vez)
    function onScroll() {
      if (hasScrolled) return;
      hasScrolled = true;

      scrollScripts.forEach(([name, config]) => {
        loadScript(name, config);
      });

      // Remover listener después de la primera ejecución
      window.removeEventListener('scroll', onScroll, { passive: true });
    }

    // Cargar scripts al interactuar (solo una vez)
    function onInteraction() {
      if (hasInteracted) return;
      hasInteracted = true;

      interactionScripts.forEach(([name, config]) => {
        loadScript(name, config);
      });

      // Remover listeners
      ['mousemove', 'touchstart', 'click'].forEach(event => {
        window.removeEventListener(event, onInteraction, { passive: true });
      });
    }

    // Agregar event listeners
    if (scrollScripts.length > 0) {
      window.addEventListener('scroll', onScroll, { passive: true });
      
      // Fallback: cargar después de 3 segundos si no hay scroll
      setTimeout(() => {
        if (!hasScrolled) onScroll();
      }, 3000);
    }

    if (interactionScripts.length > 0) {
      ['mousemove', 'touchstart', 'click'].forEach(event => {
        window.addEventListener(event, onInteraction, { passive: true, once: true });
      });

      // Fallback: cargar después de 5 segundos si no hay interacción
      setTimeout(() => {
        if (!hasInteracted) onInteraction();
      }, 5000);
    }
  }

  // Iniciar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScriptLoading);
  } else {
    initScriptLoading();
  }

  // Exponer funciones globalmente por si se necesitan
  window.lazyScripts = {
    load: loadScript,
    isLoaded: (name) => loadedScripts[name] || false
  };
})();